import { RUNTIME_FIELDS } from '../history';
import type { Edge, Node, XYPosition } from '../types';

/** What copy keeps: nodes and only the edges whose both ends were copied (Phase 4.2). */
export type ClipboardContent = { nodes: Node[]; edges: Edge[] };

/** `newNodeId` / `newEdgeId` give each copy its id (the app's `node-<uuid>` / `edge-<uuid>` format). */
export type PasteOptions = {
  offset: XYPosition;
  newNodeId: (node: Node) => string;
  newEdgeId: (edge: Edge) => string;
};

/** The nodes with `ids`, plus the edges between them; edges to nodes left behind are not copied. */
export const copyElements = (nodes: readonly Node[], edges: readonly Edge[], ids: readonly string[]): ClipboardContent => {
  const copied = new Set(ids);
  return {
    nodes: nodes.filter((node) => copied.has(node.id)),
    edges: edges.filter((edge) => copied.has(edge.source) && copied.has(edge.target)),
  };
};

/** Top-left corner of the copied nodes' positions (`null` when empty). Pasting at a point moves this corner there. */
export const getContentOrigin = ({ nodes }: ClipboardContent): XYPosition | null =>
  nodes.length === 0
    ? null
    : { x: Math.min(...nodes.map((node) => node.position.x)), y: Math.min(...nodes.map((node) => node.position.y)) };

const withoutRuntimeFields = <T extends object>(element: T): T =>
  Object.fromEntries(Object.entries(element).filter(([key]) => !RUNTIME_FIELDS.includes(key))) as T;

/** Fresh copies: new ids, positions moved by `offset`, edges re-pointed to the copied nodes, engine runtime fields dropped. */
export const pasteElements = (content: ClipboardContent, { offset, newNodeId, newEdgeId }: PasteOptions): ClipboardContent => {
  const newIds = new Map(content.nodes.map((node) => [node.id, newNodeId(node)]));
  const nodes = content.nodes.flatMap((node) => {
    const id = newIds.get(node.id);
    if (id === undefined) return [];
    return [{ ...withoutRuntimeFields(node), id, position: { x: node.position.x + offset.x, y: node.position.y + offset.y } }];
  });
  const edges = content.edges.flatMap((edge) => {
    const source = newIds.get(edge.source);
    const target = newIds.get(edge.target);
    if (source === undefined || target === undefined) return [];
    return [{ ...withoutRuntimeFields(edge), id: newEdgeId(edge), source, target }];
  });
  return { nodes, edges };
};
